import { formatDecimalPounds } from './formatDecimalPounds';
import type { PaymentLinkChannels, PaymentLinkSummary } from '../types/paymentLinks.types';

export const MOTORPLAN_CHECKING_REASON = 'Checking what the customer has left to pay.';
export const MOTORPLAN_UNAVAILABLE_REASON = 'MotorPlan Payments is unavailable';
export const MOTORPLAN_PAID_IN_FULL_REASON = 'The customer has paid in full';
export const MOTORPLAN_NO_MODE_REASON =
    "A part payment can't cover this shortfall, and only an active or on-hold plan can be paid off by link.";
export const PAY_OFF_NOT_OFFERED_REASON = 'Only an active or on-hold plan can be paid off by link.';
export const CHOOSE_CHANNEL_MESSAGE = 'Choose email, text or both';
export const SEND_BLOCKED_REASON = 'Enter a valid amount and choose email, text or both to send the link.';

export const leftToPayTooLowReason = (leftToPay: string): string =>
    `Only ${formatDecimalPounds(leftToPay)} is left to pay, which is less than the amount needed`;

export function channelPhrase({ email, sms }: PaymentLinkChannels): string {
    if (email && sms) return 'by email and text';
    return sms ? 'by text' : 'by email';
}

export const sentNotice = (amount: string, channels: PaymentLinkChannels): string =>
    `Payment link for ${formatDecimalPounds(amount)} sent ${channelPhrase(channels)}. ` +
    'The balance updates when the customer pays; come back to raise this invoice then.';

export const PAY_OFF_ONLY_EXPLANATION =
    'A part payment would leave less than £1.00 to pay, so this link pays off the plan.';
export const PAY_OFF_CONFIRM_TITLE = 'Send pay-off link';
const PAY_OFF_CONFIRM_BASE = 'Paying this link ends what the customer owes MotorPlan for this plan.';

export const payOffConfirmMessage = (planStaysActive: boolean): string =>
    planStaysActive
        ? `${PAY_OFF_CONFIRM_BASE} The plan stays active for servicing and invoicing.`
        : PAY_OFF_CONFIRM_BASE;

export const payOffHint = (leftToPay: string): string =>
    `Enter ${formatDecimalPounds(leftToPay)}, everything the customer has left to pay, to pay off the plan.`;

export const payOffSentNotice = (amount: string, channels: PaymentLinkChannels): string =>
    `Pay-off link for ${formatDecimalPounds(amount)} sent ${channelPhrase(channels)}. ` +
    'The balance updates when the customer pays; come back to raise this invoice then.';

export const linkSentNotice = (link: PaymentLinkSummary): string =>
    link.paysOff ? payOffSentNotice(link.amount, link.channels) : sentNotice(link.amount, link.channels);

const shortDate = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? 'an unknown date'
        : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const linkNoun = (link: PaymentLinkSummary): string => (link.paysOff ? 'pay-off link' : 'payment link');

export const REPLACE_LINK_TITLE = 'Replace the existing link?';
export const REPLACE_LINK_CONFIRM_LABEL = 'Send new link';
export const REPLACE_LINK_CANCEL_LABEL = 'Keep existing';

export const replaceLinkMessage = (link: PaymentLinkSummary): string =>
    `A ${linkNoun(link)} for ${formatDecimalPounds(link.amount)} was sent on ${shortDate(link.createdAt)} ` +
    `and expires on ${shortDate(link.expiresAt)}. Sending a new one will cancel it.`;

export const RESEND_LINK_LABEL = 'Send a new link';
export const CHANGE_LINK_LABEL = 'Send a different amount';
export const OTHER_PAYMENT_OPTIONS_LABEL = 'See other payment options';

export const paymentReceivedNotice = (amount: string): string =>
    `Payment of ${formatDecimalPounds(amount)} received through MotorPlan. The balance can take a minute to update.`;

export const awaitingPaymentNotice = (link: PaymentLinkSummary): string =>
    `${link.paysOff ? 'Pay-off link' : 'Payment link'} for ${formatDecimalPounds(link.amount)} sent ` +
    `${shortDate(link.createdAt)}, expires ${shortDate(link.expiresAt)}, awaiting payment.`;
